import React, { useContext } from 'react'
import { generatePath } from 'react-router-dom'
import { BonusBaseType } from '@/api/Transaction/Types/BonusTransactionDto'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormDateField,
  FormField,
  FormReadonlyField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Spinner } from '@/components/ui/spinner'
import { Alert, Tooltip } from 'antd'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { BonusType } from 'api/Transaction/Types/BonusType'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { UpdateBonusTransaction } from 'api/Transaction/Types/UpdateBonusTransaction'
import { BonusLabel } from 'api/Wallet/Types/BonusCampaignTypes'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { TransactionMenu } from 'components/TransactionMenu/TransactionMenu'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'
import { rawDateString } from 'utils/time-zone/helpers'

import { TransactionDetailsTab } from '../DetailTabs'
import { filterTransactionNotes, NoteTextList } from '../NoteList'
import { ContentField } from '../TransactionDetailsFieldHelpers'

export interface TransactionBonusFormValues {
  bonusLabelId: number
  bonusType: string
  bonusCampaignTypeId?: string
  bonusCampaignTypeName?: string
  wallet: { id: string; name: string }
  client: { id: string; name: string }
  status?: { id: number; name: string }
  reserved: number
  amount: number
  lotsRequired: number
  lotsTraded: number
  tradedProfit: number
  expiryDate: string
  comment: string
  userReason: string
}

enum BonusTransactionStatus {
  Active = 10,
  Closed = 20,
  Cancelled = 30,
  Expired = 40,
}

const TransactionDetailsBonusFormUI: React.FC<
  FormikProps<TransactionBonusFormValues> & OuterProps
> = (props) => {
  const { resetForm, errors, setAuditLogModal, setFieldValue, setEdit } = props
  const {
    transaction,
    values,
    notes,
    isEdit,
    bonusTypes,
    bonusStatuses,
    permissions,
    bonusLabels,
  } = props
  const [reversedTransaction] = transaction.reversedTransactions || []
  const setToast = useContext(ToastContext)
  const handleSubmitForm = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()

    const validationErrors = await props.validateForm()

    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(', ')
      setToast(errorToast(`Validation error: ${errorMessages}`))
      return
    }

    await props.submitForm()
  }

  const cancel = () => {
    setEdit(false)
    resetForm()
  }

  const isStatusActive = values.status?.id === BonusTransactionStatus.Active
  const bonusLabelName = bonusLabels.find(
    (bonusLabel) => bonusLabel.id === values.bonusLabelId
  )?.name
  const bonusTypeName = bonusTypes.find((bonusType) => bonusType.id === values.bonusType)?.name
  const statusName = bonusStatuses.find((status) => status.id === values.status?.id)?.name

  return (
    <MainTemplate>
      <div className='px-4 md:px-6'>
        <Form onSubmit={handleSubmitForm}>
          <div className='flex flex-col gap-4'>
            {notes.length > 0 && (
              <Alert
                type='error'
                message={
                  <>
                    There are notes related to the client(s) involved in this transaction. To view
                    please
                    <Button
                      type='button'
                      onClick={() => props.setActiveTab(TransactionDetailsTab.NOTES)}
                      variant='link'
                    >
                      Click Here
                    </Button>
                  </>
                }
              />
            )}

            <DataGrid
              rightAddon={
                <ButtonGroup>
                  {!isEdit ? (
                    <Tooltip
                      title={!isStatusActive ? 'Only active transactions can be edited' : ''}
                    >
                      <Button
                        type='button'
                        onClick={() => setEdit(true)}
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanEditTransactions) ||
                          !isStatusActive
                        }
                        data-test='edit-button'
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  ) : (
                    <>
                      <Button type='button' onClick={cancel} variant='outline'>
                        Cancel
                      </Button>
                      <Button type='submit' disabled={props.isSubmitting}>
                        Save
                        {props.isSubmitting && <Spinner data-icon='inline-start' />}
                      </Button>
                    </>
                  )}
                  <TransactionMenu
                    permissions={permissions}
                    handleAuditLog={() => setAuditLogModal(true)}
                  />
                </ButtonGroup>
              }
            >
              <DataColumn>
                <DataCard title='Transaction Overview'>
                  <FormReadonlyField label='Type' value={transaction.type.name} />
                  <FormReadonlyField
                    label='Date'
                    value={formatLocalDateUTC(transaction.createdDate, true)?.toString()}
                  />
                  <FormReadonlyField
                    label='Client'
                    value={values.client.name}
                    link={generatePath(links.recordsClients.detailsUrl as string, {
                      id: values.client.id,
                      slug: 'details',
                    })}
                  />
                  {values.wallet && (
                    <FormReadonlyField
                      label='Wallet'
                      value={values.wallet.name}
                      link={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: values.wallet.id,
                        slug: 'details',
                      })}
                    />
                  )}
                  {values.bonusCampaignTypeName && (
                    <FormReadonlyField
                      label='Automated Bonus Campaign'
                      value={values.bonusCampaignTypeName}
                      link={generatePath(links.bonusModule.detailsUrl as string, {
                        id: values.bonusCampaignTypeId,
                        slug: 'details',
                      })}
                    />
                  )}
                </DataCard>

                <DataCard title='Other'>
                  <FormTextareaField
                    label='Client Comment'
                    name='comment'
                    readOnly={!isEdit}
                    maxLength={130}
                    error={errors.comment}
                  />
                  {isEdit && (
                    <FormTextareaField
                      label='User Reason'
                      name='userReason'
                      readOnly={!isEdit}
                      maxLength={130}
                      error={errors.userReason}
                    />
                  )}
                  {transaction.reverseTransaction && (
                    <FormReadonlyField
                      label='Reverse Transaction'
                      value={transaction.reverseTransaction.name}
                      link={generatePath('/transactions/:id', {
                        id: transaction.reverseTransaction.id,
                      })}
                    />
                  )}
                  {reversedTransaction && (
                    <FormReadonlyField
                      label='Reversed Transaction'
                      value={reversedTransaction.name}
                      link={generatePath(links.transactionBonus.detailsUrl as string, {
                        id: reversedTransaction.id,
                      })}
                    />
                  )}
                </DataCard>
              </DataColumn>

              <DataColumn>
                <DataCard title='Bonus Details'>
                  <FormReadonlyField label='Bonus Label' value={bonusLabelName} />
                  <FormReadonlyField label='Bonus Type' value={bonusTypeName} />
                  <FormReadonlyField label='Status' value={statusName} />
                  <FormReadonlyField label='Reserved' value={values.reserved} />
                  <FormField
                    error={errors.amount}
                    label='Amount'
                    name='amount'
                    readOnly={!isEdit}
                  />
                  {filterTransactionNotes(notes).length > 0 && (
                    <ContentField label='Transaction Notes'>
                      <div className='w-full'>
                        <NoteTextList transactionNotes={filterTransactionNotes(notes)} />
                      </div>
                    </ContentField>
                  )}
                  <FormField
                    label='Lots Required'
                    name='lotsRequired'
                    error={errors.lotsRequired}
                    readOnly={!isEdit}
                  />
                  <FormReadonlyField label='Lots Traded' value={values.lotsTraded} />
                  <FormReadonlyField label='Profit' value={values.tradedProfit} />
                  <FormDateField
                    label='Expiry Date'
                    name='expiryDate'
                    readOnly={!isEdit}
                    error={errors.expiryDate}
                    value={values.expiryDate ? new Date(rawDateString(values.expiryDate)) : null}
                    onChange={(value) =>
                      setFieldValue('expiryDate', value ? value.toISOString() : '')
                    }
                  />
                </DataCard>
              </DataColumn>
            </DataGrid>
          </div>
        </Form>
      </div>
    </MainTemplate>
  )
}

export interface TransactionDetailsBonusGeneralProps {
  transaction: TransactionDto
  client: { id: string; name: string } | undefined
  permissions: PermissionDto[]
  bonusTypes: BonusType[]
  bonusStatuses: NameDto[]
  bonusLabels: BonusLabel[]

  setActiveTab: (tabName: string) => void
  onSubmit(values: UpdateBonusTransaction, id: string): Promise<void>
  setAuditLogModal(value: boolean): void
}
type Toast = ReturnType<typeof successToast>

interface OuterProps extends TransactionDetailsBonusGeneralProps {
  notes: TransactionNoteDto[]
  isEdit: boolean
  setEdit(val: boolean): void
  setToast: (toast: Toast) => void
}

const dataToSubmit = (values: TransactionBonusFormValues): UpdateBonusTransaction => {
  return {
    comment: values.comment,
    amount: values.amount,
    lotsRequired: values.lotsRequired,
    userReason: values.userReason,
    expiryDate: values.expiryDate,
    bonusLabelId: values.bonusLabelId,
  }
}

export const TransactionDetailsBonusGeneralForm = withFormik<
  OuterProps,
  TransactionBonusFormValues
>({
  mapPropsToValues: ({ transaction }) => {
    const walletTx = transaction?.walletTransactions?.[0]
    const tradingAccountTx = transaction?.tradingAccountTransactions?.[0]

    const useTrading = !walletTx && tradingAccountTx
    const bonusTx = walletTx?.bonusTransaction
    return {
      bonusType: bonusTx?.bonusType?.id,
      bonusCampaignTypeId: bonusTx?.bonusCampaign?.id,
      bonusCampaignTypeName: bonusTx?.bonusCampaign?.name,
      status: bonusTx?.bonusTransactionStatus,
      reserved: bonusTx?.bonusReserved ?? 0,
      amount: useTrading ? tradingAccountTx?.amount : walletTx?.amount,
      lotsRequired: bonusTx?.lotsRequired || 0,
      lotsTraded: bonusTx?.lotsTraded || 0,
      tradedProfit: bonusTx?.tradedProfit || 0,
      expiryDate: bonusTx?.expiryDate,
      client: useTrading ? tradingAccountTx?.client : walletTx?.wallet?.client,
      wallet: useTrading ? tradingAccountTx?.wallet : walletTx?.wallet,
      userReason: '',
      comment: transaction.comment || '',
      bonusLabelId: transaction?.walletTransactions?.[0]?.bonusTransaction?.bonusLabel?.id,
    }
  },
  validate: (values, props) => {
    const { transaction } = props
    const errors: FormikErrors<TransactionBonusFormValues> = {}
    const bonusTx = transaction?.walletTransactions?.[0].bonusTransaction

    if (!values.amount) errors.amount = 'Amount is required'
    if (!values.lotsRequired && bonusTx.bonusType.baseType.id !== BonusBaseType.Restricted)
      errors.lotsRequired = 'Lots Required is required'
    if (!values.expiryDate) errors.expiryDate = 'Expiry Date is required'
    if (!values.comment) errors.comment = 'Comment is required'
    if (!values.userReason) errors.userReason = 'User Reason is required'
    return errors
  },
  handleSubmit: async (values, { props, setSubmitting, resetForm }) => {
    try {
      await props.onSubmit(dataToSubmit(values), props.transaction.id)
      props.setEdit(false)
      resetForm()
      props.setToast(successToast('Transaction updated'))
    } catch (error) {
      console.error(error)
      props.setEdit(true)
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(TransactionDetailsBonusFormUI)
